package com.htmlagent.site.theme

import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.CssStyleBaseScope
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.until
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px

/**
 * The tokens the interface is written with: typed CSS properties (`StyleVariable`) that
 * [ThemeTokensStyle] sets once at the root according to the mode, and every style reads
 * without knowing which mode is active.
 *
 * The names are semantic (`surface`, `onAccent`), not color names: `cobalt9` would force a
 * rename the day the accent changes. And they use `StyleVariable` + `Modifier.setVariable`,
 * not `property("--x", …)`: Kobweb does not emit hand-written custom properties in the
 * classes it generates, and the rule disappears without an error.
 */
object Tokens {

    /** App background. Step 1. */
    val bg by StyleVariable<Color>()

    /** A panel or a card. Step 3. */
    val surface by StyleVariable<Color>()

    /** What rests on a panel and stands out. Step 4. */
    val raised by StyleVariable<Color>()

    /** What is active or selected. Step 5. */
    val active by StyleVariable<Color>()

    /** What sinks in: the composer field. Step 2. */
    val well by StyleVariable<Color>()

    /** Separator. Step 6: it outlines nothing usable, so the 3:1 does not apply. */
    val line by StyleVariable<Color>()

    /** The border of a control. Step 8, the one that meets the threshold. */
    val lineStrong by StyleVariable<Color>()

    /** Primary text. Step 12. */
    val fg by StyleVariable<Color>()

    /** Secondary text. Step 11. */
    val muted by StyleVariable<Color>()

    /** The solid: the primary button. Step 9 of the accent. */
    val accent by StyleVariable<Color>()

    /** The solid on hover. Step 10. */
    val accentHover by StyleVariable<Color>()

    /** What goes on top of the solid. */
    val onAccent by StyleVariable<Color>()

    /** Accent-tinted surface: the user bubble. Step 3 of the accent. */
    val accentSurface by StyleVariable<Color>()

    /** Accent-tinted border. Step 7. */
    val accentLine by StyleVariable<Color>()

    /**
     * The tile the product mark sits on.
     *
     * The accent-tinted surface in both modes, which is the same value [accentSurface] carries.
     * It stays a token of its own because it answers a different question -what goes behind the
     * mark- and the mark is the only thing that can decide it.
     *
     * It used to be the solid accent in dark and a neutral surface in light. Both moved: the mark
     * now fills its own face, so the tile no longer has to carry the figure, and the solid accent
     * put a second one twelve pixels from the primary button in the same bar.
     */
    val markTile by StyleVariable<Color>()

    val danger by StyleVariable<Color>()
    val working by StyleVariable<Color>()

    /**
     * The glass ([Glass]) changes with the mode too, and inverting it is not enough: in dark
     * the panel lightens over the background; in light it barely darkens and leans on the
     * shadow, because a light translucent panel over a light canvas is not distinguishable.
     */
    val glassPanelFill by StyleVariable<String>()
    val glassRaisedFill by StyleVariable<String>()
    val glassWellFill by StyleVariable<String>()
    val glassEdge by StyleVariable<String>()
    val glassDivider by StyleVariable<String>()
    val glassPanelShadow by StyleVariable<String>()
    val glassRaisedShadow by StyleVariable<String>()
    val glassWellShadow by StyleVariable<String>()

    /** The background glow, which is what makes the glass blur visible. */
    val backdrop by StyleVariable<String>()

    /**
     * A very light accent highlight: a ghost button under the mouse.
     *
     * Washes (`rgba` of a system color) are tokens and not literals in the styles: a system
     * color with transparency is still part of the system, and hand-written it would not
     * change with the mode.
     */
    val accentWash by StyleVariable<String>()

    /** The focus ring. */
    val focusRing by StyleVariable<String>()

    /** A skeleton bar currently being written. */
    val skeletonActive by StyleVariable<String>()

    /** A finished skeleton bar. */
    val skeletonDone by StyleVariable<String>()

    /** A skeleton bar that has not arrived yet. */
    val skeletonGhost by StyleVariable<String>()

    /** A neutral ornament: the frame's traffic lights, the divider handle. */
    val ornament by StyleVariable<String>()

    /** A dark veil over the glass: the code, the address bar. */
    val scrim by StyleVariable<String>()

    /**
     * What covers the whole page while a dialog is open, which is a different job from [scrim].
     *
     * It used to borrow that one and it does not fit: at 4 % in light mode the page barely
     * changed and the card, being a step DARKER than the canvas there, sank into it instead of
     * coming forward. Measured against the card: 1.03:1 at the borrowed value, 2.14:1 here.
     */
    val modalScrim by StyleVariable<String>()

    /**
     * The veil that carries a caption over a screenshot.
     *
     * It is a gradient and not a color because it has to disappear at the top: the page under it
     * is the argument, and a flat panel would hide a third of it. It follows the band, so inside
     * the ink one it stays dark even in light mode.
     */
    val shotVeil by StyleVariable<String>()

    /** The dark edge separating the send button from the field. */
    val solidRing by StyleVariable<String>()

    /** Background and border of a destructive confirmation. */
    val dangerWash by StyleVariable<String>()
    val dangerLine by StyleVariable<String>()
    val dangerWashHover by StyleVariable<String>()

    /**
     * Spacing in steps of four, a single progression for the whole interface.
     *
     * Measurements do not change with the mode and are tokens anyway: they show up named in
     * the inspector and can be redefined for a specific area.
     */
    val spaceXs by StyleVariable<CSSLengthNumericValue>()

    /** Half a step, for the padding that only separates a border from its content. */
    val spaceXxs by StyleVariable<CSSLengthNumericValue>()
    val spaceSm by StyleVariable<CSSLengthNumericValue>()
    val spaceMd by StyleVariable<CSSLengthNumericValue>()
    val spaceLg by StyleVariable<CSSLengthNumericValue>()
    val spaceXl by StyleVariable<CSSLengthNumericValue>()
    val spaceXxl by StyleVariable<CSSLengthNumericValue>()
    val spaceXxxl by StyleVariable<CSSLengthNumericValue>()

    /** Type scale with a 1.25 ratio from 16px: few steps, well apart. */
    val textXs by StyleVariable<CSSLengthNumericValue>()
    val textSm by StyleVariable<CSSLengthNumericValue>()
    val textBase by StyleVariable<CSSLengthNumericValue>()
    val textMd by StyleVariable<CSSLengthNumericValue>()
    val textLg by StyleVariable<CSSLengthNumericValue>()
    val textXl by StyleVariable<CSSLengthNumericValue>()

    /**
     * A section headline on the landing. Past the interface scale on purpose: the application
     * never needs a size this loud, and the landing needs nothing else to build a rhythm.
     */
    val textDisplay by StyleVariable<CSSLengthNumericValue>()

    /** The one headline that has to be seen before it is read. */
    val textHero by StyleVariable<CSSLengthNumericValue>()

    /** The interface family and the code one, where alignment carries information. */
    val fontSans by StyleVariable<String>()
    val fontMono by StyleVariable<String>()

    /** The wordmark's family. Nothing else uses it: see [Fonts.Display]. */
    val fontDisplay by StyleVariable<String>()

    /** Concentric radii: what sits inside a card gets less radius than the card. */
    val radiusSm by StyleVariable<CSSLengthNumericValue>()

    /** A pill: a radius large enough that the shape is the height, not the number. */
    val radiusFull by StyleVariable<CSSLengthNumericValue>()
    val radiusMd by StyleVariable<CSSLengthNumericValue>()
    val radiusLg by StyleVariable<CSSLengthNumericValue>()

    /**
     * A secondary control: a turn action, a segment.
     *
     * Control heights are decided here and Silk's styles are rewritten to read them;
     * otherwise Silk imposes its own size and `ButtonSize` has to be repeated at every call.
     */
    val controlSm by StyleVariable<CSSLengthNumericValue>()

    /** A regular control: a rail button. */
    val controlMd by StyleVariable<CSSLengthNumericValue>()

    /** A primary touch target. 44px is the accessibility minimum for touch. */
    val controlLg by StyleVariable<CSSLengthNumericValue>()

    /**
     * The icon of a button with text beside it.
     *
     * The pieces below are tokens because they repeat in several places. What is used only
     * once (the divider handle, the composer's max height) stays as a literal in its style:
     * it belongs to no progression.
     */
    val iconSm by StyleVariable<CSSLengthNumericValue>()

    /** The icon that sits inside a small piece: an avatar, a chip, a receipt line. */
    val iconXs by StyleVariable<CSSLengthNumericValue>()

    /** The icon of an icon-only button. */
    val iconMd by StyleVariable<CSSLengthNumericValue>()

    /** The icon inside the send button. */
    val iconLg by StyleVariable<CSSLengthNumericValue>()

    /** A status dot: the activity one, the pill one. */
    val dot by StyleVariable<CSSLengthNumericValue>()

    /** An ornamental dot, which is bigger because it says nothing: the browser traffic lights. */
    val dotOrnament by StyleVariable<CSSLengthNumericValue>()

    /** The agent avatar and the rail mark share a family, not a size. */
    val avatar by StyleVariable<CSSLengthNumericValue>()

    /** The rail mark: the same family as [avatar], one step up. */
    val mark by StyleVariable<CSSLengthNumericValue>()

    /** A one-pixel line. Sounds trivial until someone writes 2 somewhere. */
    val hairline by StyleVariable<CSSLengthNumericValue>()

    /** The width the preview takes when it is shown as a phone. */
    val previewMobile by StyleVariable<CSSLengthNumericValue>()

    /**
     * The bars of the loading skeleton, named after what each one stands in for.
     *
     * They are tokens and not numbers in the component because the skeleton is a drawing of the
     * real page: when the type scale moves, the fake lines have to move with it or the sketch
     * stops resembling what arrives after it.
     */
    val barLine by StyleVariable<CSSLengthNumericValue>()

    /** A secondary line: a caption, a date, a size. */
    val barMeta by StyleVariable<CSSLengthNumericValue>()

    /** A small label above a block. */
    val barLabel by StyleVariable<CSSLengthNumericValue>()

    /** A heading. */
    val barHeading by StyleVariable<CSSLengthNumericValue>()

    /** A button. */
    val barButton by StyleVariable<CSSLengthNumericValue>()

    /** A card, which is the tallest thing the sketch draws. */
    val barCard by StyleVariable<CSSLengthNumericValue>()
}

/** What does not depend on the mode: measurements, type and radii. Declared once, composed with [colors]. */
private fun Modifier.metrics(): Modifier = this
    .setVariable(Tokens.spaceXs, 4.px)
    .setVariable(Tokens.spaceXxs, Space.Xxs)
    .setVariable(Tokens.spaceSm, 8.px)
    .setVariable(Tokens.spaceMd, 12.px)
    .setVariable(Tokens.spaceLg, 16.px)
    .setVariable(Tokens.spaceXl, 24.px)
    .setVariable(Tokens.spaceXxl, 32.px)
    .setVariable(Tokens.spaceXxxl, 48.px)
    .setVariable(Tokens.textXs, 0.769.cssRem)
    .setVariable(Tokens.textSm, 0.877.cssRem)
    .setVariable(Tokens.textBase, 1.cssRem)
    .setVariable(Tokens.textMd, 1.25.cssRem)
    .setVariable(Tokens.textLg, 1.563.cssRem)
    .setVariable(Tokens.textXl, 1.953.cssRem)
    .setVariable(Tokens.textDisplay, TypeScale.Display)
    .setVariable(Tokens.textHero, TypeScale.Hero)
    .setVariable(Tokens.fontSans, Fonts.SansCss)
    .setVariable(Tokens.fontMono, Fonts.MonoCss)
    .setVariable(Tokens.fontDisplay, Fonts.DisplayCss)
    .setVariable(Tokens.radiusSm, 8.px)
    .setVariable(Tokens.radiusFull, Radius.Full)
    .setVariable(Tokens.radiusMd, 12.px)
    .setVariable(Tokens.radiusLg, 16.px)
    .setVariable(Tokens.controlSm, 32.px)
    .setVariable(Tokens.controlMd, 40.px)
    .setVariable(Tokens.controlLg, 44.px)
    .setVariable(Tokens.iconXs, 13.px)
    .setVariable(Tokens.iconSm, 15.px)
    .setVariable(Tokens.iconMd, 18.px)
    .setVariable(Tokens.iconLg, 20.px)
    .setVariable(Tokens.dot, 7.px)
    .setVariable(Tokens.dotOrnament, 10.px)
    .setVariable(Tokens.avatar, 22.px)
    .setVariable(Tokens.mark, 36.px)
    .setVariable(Tokens.hairline, 1.px)
    .setVariable(Tokens.previewMobile, 420.px)
    .setVariable(Tokens.barLine, 12.px)
    .setVariable(Tokens.barMeta, 14.px)
    .setVariable(Tokens.barLabel, 18.px)
    .setVariable(Tokens.barHeading, 26.px)
    .setVariable(Tokens.barButton, 38.px)
    .setVariable(Tokens.barCard, 84.px)

/**
 * The only place where [Tokens] values are set. It applies to the shell and everything
 * inside inherits.
 *
 * `colorMode` is in scope inside the `CssStyle`, so the block is evaluated once per mode and
 * Silk generates both rules; switching mode only changes which class is active.
 */
val ThemeTokensStyle = CssStyle {
    base { Modifier.metrics().colors(colorMode.isLight) }

    // Below a tablet, four of them shrink. The base above is untouched.
    until(Breakpoint.MD) { Modifier.compactMetrics() }
}

/**
 * What a narrow screen gives up: the two largest spacing steps and the two display sizes.
 *
 * It gives up nothing else, and the omissions are the point. Body text stays put because it is
 * in `rem` and already follows the size the person set in their browser; shrinking it with the
 * viewport would override a decision that is theirs. Controls and icons stay put because
 * [Tokens.controlLg] is the 44px minimum touch target, and a phone is where that minimum
 * matters most: scaling it down would be exactly backwards.
 *
 * It is four variables and not a single component because that is what tokens buy: the whole
 * interface reflows from one place. It is written as `until(MD)` and not as a mobile-first base
 * so the roomy values stay the ones declared in [metrics]: the narrow screen is the exception.
 */
private fun Modifier.compactMetrics(): Modifier = this
    .setVariable(Tokens.spaceXxl, Space.Xl)
    .setVariable(Tokens.spaceXxxl, Space.Xxl)
    .setVariable(Tokens.textLg, TypeScale.Md)
    .setVariable(Tokens.textXl, TypeScale.Lg)
    .setVariable(Tokens.textDisplay, TypeScale.Xl)
    .setVariable(Tokens.textHero, TypeScale.Xxl)

/**
 * Repaints a subtree with the dark palette, whatever mode the page is in.
 *
 * It is what the landing's alternating bands are made of, and it adds no token: a band is not
 * a new set of colors, it is the same set resolved the other way. Everything inside goes on
 * reading [Tokens.surface] or [Tokens.fg] and lands on the right value, which is the whole
 * point of having the tokens in the first place.
 */
fun Modifier.darkTokens(): Modifier = colors(isLight = false)

/** The values that depend on the color mode. */
@Suppress("LongMethod") // One assignment per token: it is a list, not logic.
private fun Modifier.colors(isLight: Boolean): Modifier {
    if (isLight) {
        return this
            .setVariable(Tokens.bg, Slate.Light.S1)
            .setVariable(Tokens.well, Slate.Light.S2)
            .setVariable(Tokens.surface, Slate.Light.S3)
            .setVariable(Tokens.raised, Slate.Light.S4)
            .setVariable(Tokens.active, Slate.Light.S5)
            .setVariable(Tokens.line, Slate.Light.S6)
            .setVariable(Tokens.lineStrong, Slate.Light.S8)
            .setVariable(Tokens.muted, Slate.Light.S11)
            .setVariable(Tokens.fg, Slate.Light.S12)
            .setVariable(Tokens.accent, Cobalt.Light.S9)
            .setVariable(Tokens.accentHover, Cobalt.Light.S10)
            .setVariable(Tokens.onAccent, Cobalt.Light.OnSolid)
            .setVariable(Tokens.accentSurface, Cobalt.Light.S3)
            .setVariable(Tokens.accentLine, Cobalt.Light.S7)
            .setVariable(Tokens.markTile, Cobalt.Light.S3)
            .setVariable(Tokens.danger, Status.Light.Danger)
            .setVariable(Tokens.working, Status.Light.Working)
            .setVariable(Tokens.glassPanelFill, "rgba(255, 255, 255, 0.72)")
            .setVariable(Tokens.glassRaisedFill, "rgba(6, 103, 212, 0.10)")
            .setVariable(Tokens.glassWellFill, "rgba(18, 26, 42, 0.04)")
            .setVariable(
                Tokens.shotVeil,
                "linear-gradient(180deg, rgba(251, 252, 254, 0) 0%, " +
                    "rgba(251, 252, 254, 0.36) 26%, rgba(251, 252, 254, 0.90) 100%)",
            )
            .setVariable(Tokens.glassEdge, "1px solid rgba(18, 26, 42, 0.10)")
            .setVariable(Tokens.glassDivider, "1px solid rgba(18, 26, 42, 0.07)")
            .setVariable(
                Tokens.glassPanelShadow,
                "inset 0 1px 0 rgba(255, 255, 255, 0.85), 0 12px 32px rgba(18, 26, 42, 0.10)",
            )
            .setVariable(Tokens.glassRaisedShadow, "inset 0 1px 0 rgba(255, 255, 255, 0.6)")
            .setVariable(Tokens.glassWellShadow, "inset 0 1px 3px rgba(18, 26, 42, 0.08)")
            .setVariable(
                Tokens.backdrop,
                "radial-gradient(900px 640px at 22% 8%, rgba(6, 103, 212, 0.10), transparent 68%), " +
                    "radial-gradient(1000px 700px at 72% 78%, rgba(6, 103, 212, 0.08), transparent 66%)",
            )
            .setVariable(Tokens.accentWash, "rgba(6, 103, 212, 0.08)")
            .setVariable(Tokens.focusRing, "rgba(6, 103, 212, 0.30)")
            .setVariable(Tokens.skeletonActive, "rgba(6, 103, 212, 0.22)")
            .setVariable(Tokens.skeletonDone, "rgba(18, 26, 42, 0.10)")
            .setVariable(Tokens.skeletonGhost, "rgba(18, 26, 42, 0.05)")
            .setVariable(Tokens.ornament, "rgba(18, 26, 42, 0.16)")
            .setVariable(Tokens.scrim, "rgba(18, 26, 42, 0.04)")
            .setVariable(Tokens.modalScrim, "rgba(18, 26, 42, 0.38)")
            .setVariable(Tokens.solidRing, "rgba(18, 26, 42, 0.12)")
            .setVariable(Tokens.dangerWash, "rgba(179, 33, 58, 0.06)")
            .setVariable(Tokens.dangerLine, "rgba(179, 33, 58, 0.40)")
            .setVariable(Tokens.dangerWashHover, "rgba(179, 33, 58, 0.12)")
    } else {
        return this
            .setVariable(Tokens.bg, Slate.Dark.S1)
            .setVariable(Tokens.well, Slate.Dark.S2)
            .setVariable(Tokens.surface, Slate.Dark.S3)
            .setVariable(Tokens.raised, Slate.Dark.S4)
            .setVariable(Tokens.active, Slate.Dark.S5)
            .setVariable(Tokens.line, Slate.Dark.S6)
            .setVariable(Tokens.lineStrong, Slate.Dark.S8)
            .setVariable(Tokens.muted, Slate.Dark.S11)
            .setVariable(Tokens.fg, Slate.Dark.S12)
            .setVariable(Tokens.accent, Cobalt.Dark.S9)
            .setVariable(Tokens.accentHover, Cobalt.Dark.S10)
            .setVariable(Tokens.onAccent, Cobalt.Dark.OnSolid)
            .setVariable(Tokens.accentSurface, Cobalt.Dark.S3)
            .setVariable(Tokens.accentLine, Cobalt.Dark.S7)
            .setVariable(Tokens.markTile, Cobalt.Dark.S3)
            .setVariable(Tokens.danger, Status.Dark.Danger)
            .setVariable(Tokens.working, Status.Dark.Working)
            .setVariable(Tokens.glassPanelFill, "rgba(80, 149, 255, 0.10)")
            .setVariable(Tokens.glassRaisedFill, "rgba(80, 149, 255, 0.16)")
            .setVariable(Tokens.glassWellFill, "rgba(6, 16, 33, 0.45)")
            .setVariable(
                Tokens.shotVeil,
                "linear-gradient(180deg, rgba(7, 13, 26, 0) 0%, " +
                    "rgba(7, 13, 26, 0.34) 26%, rgba(7, 13, 26, 0.88) 100%)",
            )
            .setVariable(Tokens.glassEdge, "1px solid rgba(231, 237, 247, 0.12)")
            .setVariable(Tokens.glassDivider, "1px solid rgba(231, 237, 247, 0.07)")
            .setVariable(
                Tokens.glassPanelShadow,
                "inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 24px 60px rgba(0, 0, 0, 0.45)",
            )
            .setVariable(Tokens.glassRaisedShadow, "inset 0 1px 0 rgba(255, 255, 255, 0.06)")
            .setVariable(Tokens.glassWellShadow, "inset 0 1px 3px rgba(0, 0, 0, 0.35)")
            .setVariable(
                Tokens.backdrop,
                "radial-gradient(900px 640px at 22% 8%, rgba(80, 149, 255, 0.30), transparent 68%), " +
                    "radial-gradient(1000px 700px at 72% 78%, rgba(27, 123, 234, 0.26), transparent 66%), " +
                    "radial-gradient(760px 560px at 88% 12%, rgba(0, 88, 164, 0.30), transparent 70%)",
            )
            .setVariable(Tokens.accentWash, "rgba(80, 149, 255, 0.10)")
            .setVariable(Tokens.focusRing, "rgba(80, 149, 255, 0.35)")
            .setVariable(Tokens.skeletonActive, "rgba(80, 149, 255, 0.34)")
            .setVariable(Tokens.skeletonDone, "rgba(231, 237, 247, 0.16)")
            .setVariable(Tokens.skeletonGhost, "rgba(231, 237, 247, 0.05)")
            .setVariable(Tokens.ornament, "rgba(231, 237, 247, 0.18)")
            .setVariable(Tokens.scrim, "rgba(6, 16, 33, 0.55)")
            // The value the dialogs already had here, which works: in dark the veil takes the
            // page nearly to black and the card is the lighter thing on screen.
            .setVariable(Tokens.modalScrim, "rgba(6, 16, 33, 0.55)")
            .setVariable(Tokens.solidRing, "rgba(6, 16, 33, 0.45)")
            .setVariable(Tokens.dangerWash, "rgba(255, 154, 168, 0.08)")
            .setVariable(Tokens.dangerLine, "rgba(255, 154, 168, 0.45)")
            .setVariable(Tokens.dangerWashHover, "rgba(255, 154, 168, 0.14)")
    }
}
